import { useState, useEffect, useCallback } from 'react'
import { channelsAPI } from '../services/api'

/**
 * useUnreadChannels - Hook to fetch and poll unread channel count for admins
 * 
 * Automatically fetches unread count when user is admin and authenticated
 * Polls every 10 seconds to keep count updated (reduced from 30s for better UX)
 * Provides manual refresh function for immediate updates after marking channels as read
 * 
 * @param {boolean} isAuthenticated - User authentication status
 * @param {Object} user - User object with isAdmin property
 * @param {number} pollInterval - Polling interval in milliseconds (default: 10000)
 * @returns {Object} { unreadCount: number, refreshUnreadCount: function }
 */
export function useUnreadChannels(isAuthenticated, user, pollInterval = 10000) {
  const [unreadCount, setUnreadCount] = useState(0)

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated || !user?.isAdmin) {
      setUnreadCount(0)
      return
    }

    try {
      // Double-check auth state right before API call (race condition guard)
      if (!isAuthenticated || !user?.isAdmin) {
        return
      }
      const response = await channelsAPI.getUserChannels({ silentError: true })
      const channels = response.data.channels || []
      const totalUnread = channels.reduce((sum, ch) => sum + (ch.unreadCount || 0), 0)
      setUnreadCount(totalUnread)
    } catch (error) {
      // Silently handle errors during background polling
      // Network errors when app is backgrounded are expected
    }
  }, [isAuthenticated, user])

  useEffect(() => {
    if (!isAuthenticated || !user?.isAdmin) {
      setUnreadCount(0)
      return
    }

    fetchUnreadCount()
    const interval = setInterval(fetchUnreadCount, pollInterval)
    
    // Resume polling when tab becomes visible (fixes notification badge stuck when tab was backgrounded)
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated && user?.isAdmin) {
        fetchUnreadCount() // Immediate refresh when tab becomes active
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isAuthenticated, user, pollInterval, fetchUnreadCount])

  return { unreadCount, refreshUnreadCount: fetchUnreadCount }
}

